import React from 'react';
import { Badge, Card, Image, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import SplitPreviewModal from '../modals/SplitPreview.modal';
import { FaCircleExclamation } from "react-icons/fa6";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../theme/useTheme';

interface ICsrReportTable {
  data: any[];
  pageNumber: number;
  pageSize: number;
  loading?: boolean;
  reload?: () => void;
}

const CsrReportTable = ({ data, pageNumber, pageSize, loading = false, reload }: ICsrReportTable) => {
  const { colors } = useTheme();

  const [splitPreview, setSplitPreview] = React.useState<any>(null);

  console.log("splitPreview", splitPreview);

  function getHeaders() {
    if (!data || !data.length) return [];

    let keys = new Set<string>();

    Object.entries(data[0]).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        keys.add(key);
      } else if (key === 'verificationDetails' && Array.isArray(value)) {
        value.forEach((verification: any, idx: number) => {
          if (verification.method) {
            keys.add(`Verification (${verification.method === "fingerprint" ? "Right Fingerprint" : verification.method === "fingerprint2" ? "Left Fingerprint" : verification.method})`);
          }
        });
      }
    });
    return Array.from(keys);
  }


  const finger_name = [
    { "label": "Thumb", "value": "thumb" },
    { "label": "Index Finger", "value": "index_finger" },
    { "label": "Middle Finger", "value": "middle_finger" },
    { "label": "Ring Finger", "value": "ring_finger" },
    { "label": "Little Finger", "value": "little_finger" }
  ]

  const hand_data = [
    { "label": "Left Hand", "value": "fingerprint2" },
    { "label": "Right Hand", "value": "fingerprint" }
  ]

  return (
    <>
      <Card
        className="mt-3"
        style={{
          backgroundColor: colors.background.primary,
          borderRadius: "12px",
          border: `1px solid ${colors.border.secondary}`,
          boxShadow: colors.shadow,
        }}
      >
        <Card.Body>
          {
            !loading &&
            <div className="d-flex align-items-center justify-content-end mb-3">
              <FontAwesomeIcon
                icon={faRefresh}
                className='m-2 cursor-pointer'
                onClick={reload}
                style={{ color: colors.primary.main, transition: 'all 0.3s ease' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.7';
                  e.currentTarget.style.transform = 'rotate(180deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              />
            </div>
          }
          <div style={{ overflowX: 'auto' }}>
            <Table striped bordered hover size="sm" responsive style={{ marginBottom: 0 }}>
              <thead>
                <tr style={{ backgroundColor: colors.background.secondary, borderBottom: `2px solid ${colors.border.primary}` }}>
                  {loading ? (
                    <></>
                  ) : (
                    getHeaders().map((header, index) => (
                      <th
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: colors.text.primary,
                          backgroundColor: colors.background.secondary,
                          borderColor: colors.border.secondary,
                          padding: '0.75rem',
                          whiteSpace: 'nowrap'
                        }}
                        key={index}
                      >
                        {header && header == "Zone" ? "Division" : header === "Live Photo" ? "Live Image" : header === "Center" ? "Venue Id" : header === "Center Name" ? "Venue Name" : header}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={getHeaders().length} style={{ textAlign: 'center', padding: '1rem', color: colors.text.secondary }}>
                      <span>Loading data... <Spinner animation="border" size="sm" /></span>
                    </td>
                  </tr>
                ) : data && data.length > 0 ? (
                  data.map((item, index) => (
                    <tr
                      key={item?._id || index}
                      style={{
                        backgroundColor: index % 2 === 0 ? colors.background.primary : colors.background.tertiary,
                        borderColor: colors.border.light,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.background.secondary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? colors.background.primary : colors.background.tertiary;
                      }}
                    >
                      {getHeaders().map((header, idx) => {
                        if (header === "Live Photo" || header === "Photo") {
                          const photoUrl = item[header];
                          return (
                            <td
                              key={idx}
                              style={{
                                borderColor: colors.border.light,
                                padding: '0.75rem',
                                fontSize: '0.875rem'
                              }}
                            >
                              <span className="cursor-pointer">
                                <Image src={photoUrl} height="40" width="40" onClick={() => setSplitPreview({ livePhoto: item["Live Photo"], uploadPhoto: item["Photo"] })} />
                              </span>
                            </td>
                          );
                        }

                        else if (header.startsWith("Verification (") && Array.isArray(item.verificationDetails)) {
                          let method = header.replace("Verification (", "").replace(")", "");
                          if (method === "Right Fingerprint") method = "fingerprint";
                          if (method === "Left Fingerprint") method = "fingerprint2";
                          const verification = item.verificationDetails.find((v: any) => v.method === method);
                          return (
                            <td
                              key={idx}
                              style={{
                                borderColor: colors.border.light,
                                padding: '0.75rem',
                                fontSize: '0.875rem',
                                fontStyle: 'italic',
                                color: colors.text.primary
                              }}
                            >
                              {
                                method === "facial"
                                  ? verification?.value
                                  : verification
                                    ? method === "aadhar-finger"
                                      ? (verification.value === "Verified" ? <span style={{ color: colors.status.success, fontWeight: 500 }}>Successful</span> : <span style={{ color: colors.status.error, fontWeight: 500 }}>Unsuccessful</span>)
                                      : (verification.value === "Verified" ? <span style={{ color: colors.status.success, fontWeight: 500 }}>Captured</span> : <span style={{ color: colors.status.error, fontWeight: 500 }}>Exception</span>)
                                    : "N/A"
                              }

                              {verification && verification.remarks &&
                                <OverlayTrigger
                                  placement="bottom"
                                  delay={{ show: 250, hide: 400 }}
                                  overlay={
                                    <Tooltip id="button-tooltip">
                                      <span>
                                        Finger: {finger_name?.find((data: any) => data?.value === JSON.parse(verification?.remarks)?.fingersData)?.label || "thumb"}<br />
                                        Remarks: {JSON.parse(verification?.remarks)?.remaks || "N/A"}<br />
                                      </span>
                                    </Tooltip>
                                  }
                                >
                                  <span>
                                    <FaCircleExclamation style={{ color: colors.status.success, cursor: "pointer", marginLeft: '0.5rem' }} size={14} />
                                  </span>
                                </OverlayTrigger>

                              }
                            </td>
                          );
                        }

                        else {
                          return (
                            <td
                              key={idx}
                              style={{
                                borderColor: colors.border.light,
                                padding: '0.75rem',
                                fontSize: '0.875rem',
                                fontStyle: 'italic',
                                color: colors.text.primary
                              }}
                            >
                              {item[header] || "N/A"}
                            </td>
                          );
                        }
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={getHeaders().length} style={{ textAlign: 'center', padding: '1rem', color: colors.text.secondary }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>No item found</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      <SplitPreviewModal
        show={splitPreview}
        handleClose={() => setSplitPreview(null)}
      />
    </>
  );
};

export default CsrReportTable;
